
import { Component, Inject, Vue } from "vue-property-decorator";
import { Container } from "inversify";
import { AuthStore } from "../../../../../common/src/stores/AuthStore";
import {
    hasActiveQuiltySubscription,
    hasActiveSubscription,
    Order,
    OrderStatus,
    OrderType,
    QuiltySubscriptionPlan,
    QuiltySubscriptionPlanType,
    TransactionType,
} from "@gsx/common";
import { NotificationStore } from "../../../../../common/src/stores/NotificationStore";
import { PaymentService } from "../../../../../landing/src/utility/payment/PaymentService";
import { OrderListStore } from "../../../../../landing/src/stores/order/OrderListStore";
import { event } from "vue-gtag";
import { AdvertisingQuoteModalStore } from "../../../../../common/src/advertising-quote/AdvertisingQuoteModalStore";
import { SubscriptionPlan } from "@gsx/common/src/types/models/subscription";
import {
    getUserQuiltySubscriptionPlan,
    getUserSubscriptionPlan,
} from "../../../../../landing/src/utility/entity/user";
import { handleError } from "../../../../../common/src/utility/errorUtils";
import AdvertisingQuoteModal from "../../../../../common/src/advertising-quote/AdvertisingQuoteModal.vue";
import LoadingOverlay from "../../../../../common/src/components/LoadingOverlay.vue";
import NoRecords from "../../../../../common/src/components/NoRecords.vue";
import PlanCard from "../../../../../landing/src/molecules/components/PlanCard.vue";
import { SubscriptionQuoteModalStore } from "../../../../../common/src/subscription-quote/SubscriptionQuoteModalStore";
import { Api } from "../../../../../common/src/api/Api";

@Component({
    components: {
        PlanCard,
        NoRecords,
        LoadingOverlay,
        AdvertisingQuoteModal,
    },
    filters: {},
})
export default class Subscriptions extends Vue {
    [x: string]: any;
    @Inject("container") readonly container!: Container;
    readonly authStore: AuthStore = this.container.get(AuthStore);
    readonly notificationStore: NotificationStore = this.container.get(NotificationStore);
    readonly paymentService: PaymentService = this.container.get(PaymentService);
    readonly listStore: OrderListStore = this.container.get(OrderListStore);
    readonly advertisingQuoteModalStore: AdvertisingQuoteModalStore = this.container.get(
        AdvertisingQuoteModalStore,
    );

    readonly modalStore: SubscriptionQuoteModalStore = this.container.get(
        SubscriptionQuoteModalStore,
    );
    readonly api: Api = this.container.get(Api);

    modalVisible: boolean = false;
    pdfPath: string = "../../../../../common/images/PDFs/Global_Space_Exchange_Site_Terms_V2.0.pdf"; // Replace with your actual PDF path
    //pdfPath: string = "../../../../../common/images/EnterpriseMembershipCompare.png";
    //client/common/images/EnterpriseMembershipCompare.png
    showModal() {
        //@ts-ignore
        $("#pdfModal").modal();
    }

    closeModal() {
        this.modalVisible = false;
    }

    isPurchasingBook: boolean = false;

    created() {
        this.listStore.load();
    }

    get subscriptionPlans(): ((SubscriptionPlan | QuiltySubscriptionPlan) & {
        isActive?: boolean;
    })[] {
        let plans = [];

        const gseSub = getUserSubscriptionPlan(this.authStore.user);
        const quiltySub = getUserQuiltySubscriptionPlan(this.authStore.user);

        if (gseSub) {
            // @ts-ignore
            gseSub.isActive = hasActiveSubscription(this.authStore.user);

            if (gseSub.name === "Financial") {
                gseSub.name = "Enterprise";
            }

            plans.push(gseSub);
        }

        if (quiltySub) {
            // @ts-ignore
            gseSub.isActive = hasActiveQuiltySubscription(this.authStore.user);
            plans.push(quiltySub);
        }

        return plans;
    }

    get isSubscriptionActive(): boolean {
        return hasActiveSubscription(this.authStore.user);
    }

    get isQuiltySubscriptionActive(): boolean {
        return hasActiveQuiltySubscription(this.authStore.user);
    }

    get isQuiltySubBasic(): boolean {
        const quiltySub = getUserQuiltySubscriptionPlan(this.authStore.user);
        if (quiltySub) {
            return quiltySub.plan === QuiltySubscriptionPlanType.Basic;
        } else return false;
    }

    get isQuiltySubIntelligence(): boolean {
        const quiltySub = getUserQuiltySubscriptionPlan(this.authStore.user);
        if (quiltySub) {
            return quiltySub.plan === QuiltySubscriptionPlanType.Intelligence;
        } else return false;
    }

    get isQuiltySubEnterprise(): boolean {
        const quiltySub = getUserQuiltySubscriptionPlan(this.authStore.user);
        if (quiltySub) {
            return quiltySub.plan === QuiltySubscriptionPlanType.Enterprise;
        } else return false;
    }

    get isUserGseEnterprise(): boolean {
        const gseSub = getUserSubscriptionPlan(this.authStore.user);
        return (
            gseSub?.name.toLowerCase() === "financial" ||
            gseSub?.name.toLowerCase() === "enterprise"
        );
    }

    get noRecords(): boolean {
        const items = this.listStore.items;
        return Boolean(items && !items.length);
    }

    get orders(): Order[] {
        return this.listStore.items ?? [];
    }

    badgeClass(order: Order) {
        return {
            "badge-accent": order.status === OrderStatus.RequestReceived,
            "badge-info": order.status === OrderStatus.InProcess,
            "badge-success": [OrderStatus.Delivered, OrderStatus.QuoteSent].includes(order.status),
            "badge-danger": [OrderStatus.Shipped].includes(order.status),
        };
    }

    openAdvertisingQuoteRequestModal() {
        this.advertisingQuoteModalStore.open();
    }

    subscribeUser() {
        this.modalStore.open();
    }

    async purchaseBook() {
        this.isPurchasingBook = true;

        try {
            await this.paymentService.purchaseProduct(TransactionType.SpaceInsight2020);
            event("Purchase", {
                eventCategory: "Purchase",
                eventAction: OrderType.SpaceInsight2020,
                eventLabel: OrderType.SpaceInsight2020,
            });
        } catch (e: any) {
            this.notificationStore.warning("Could not initialize a payment process.");
            handleError(e);
        } finally {
            this.isPurchasingBook = false;
        }
    }

    formatRequestType(input: string): string {
        return input
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    }

    capitalize(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    formatGsePlanType(plan: string): string {
        if (plan === "gse_enterprise" || plan === "financial") return "Enterprise";
        else return this.capitalize(plan);
    }

    async onQuiltyBasicSubscriptionClick() {
        try {
            await this.paymentService.purchasePlan(QuiltySubscriptionPlanType.Basic);
        } catch (e) {
            this.notificationStore.warning("Could not initialize a payment process.");
        }
    }

    async onQuiltyIntelligenceSubscriptionClick() {
        try {
            await this.paymentService.purchasePlan(QuiltySubscriptionPlanType.Intelligence);
        } catch (e) {
            this.notificationStore.warning("Could not initialize a payment process.");
        }
    }

    async onQuiltyEnterpriseSubscriptionClick() {
        try {
            this.modalStore.open("quilty");
        } catch (e) {
            this.notificationStore.warning("Could not initialize a payment process.");
        }
    }

    async onCancelQuiltySubscriptionClick() {
        try {
            await this.api.dashboard.subscription.delete(
                this.authStore.user!.quiltySubscription!.stripeId!,
            );
            setTimeout(() => {
                this.authStore.me();
                this.notificationStore.success("Subscription Canceled");
            }, 4000);
        } catch (err) {
            this.notificationStore.warning("Could not delete subscription.");
        }
    }
}
